import { useState, useEffect } from "react";
import { Link, useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/auth";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertCircle, ChevronDown } from "lucide-react";
import { useFacebookLogin } from "@/services/auth.service";
import { useSetAtom } from "jotai";
import { authStateAtom } from "@/stores/auth.atom";
import { toast } from "@/components/ui/use-toast";
import { useAuth, useUserPreferences } from "@/hooks";
import { authApi } from "@/services/auth";

// Global Facebook SDK initialization
(function() {
  // Load the SDK asynchronously
  const script = document.createElement('script');
  script.src = "https://connect.facebook.net/en_US/sdk.js";
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);

  // Initialize the SDK upon load
  window.fbAsyncInit = function() {
    window.FB?.init({
      appId: import.meta.env.VITE_FACEBOOK_APP_ID,
      cookie: true,
      xfbml: true,
      version: 'v18.0'
    });
  };
})();

export default function LoginPage() {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [language, setLanguage] = useState("ENG");
  const [invitationCode, setInvitationCode] = useState<string | null>(null);
  const [fbSDKReady, setFbSDKReady] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const setAuthState = useSetAtom(authStateAtom);

  // Local Storage Hooks
  const { isAuthenticated } = useAuth();
  const { initializePreferences } = useUserPreferences();

  // OAuth handling
  const { handleOAuthCallback, error: authError } = useAuthStore();
  const [searchParams] = useSearchParams();

  // TanStack Query hooks
  const facebookLoginMutation = useFacebookLogin();

  // Initialize app preferences
  useEffect(() => {
    initializePreferences();
  }, [initializePreferences]);

  // Check if user is already authenticated and redirect
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/feed');
    }
  }, [isAuthenticated, navigate]);

  // Check for invitation code in location state
  useEffect(() => {
    if (location.state?.invitationCode) {
      setInvitationCode(location.state.invitationCode);
      setAcceptedTerms(true);
    }
  }, [location.state]);

  // Handle Google OAuth callback
  useEffect(() => {
    const code = searchParams.get("code");
    if (code) {
      handleOAuthCallback(code).catch((err: Error) => {
        console.error("OAuth callback error:", err);
      });
    }
  }, [searchParams, handleOAuthCallback]);

  // Check if Facebook SDK is ready
  useEffect(() => {
    const checkFBSDK = () => {
      if (window.FB && typeof window.FB.login === 'function') {
        setFbSDKReady(true);
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkFBSDK()) return;

    // If not ready, set up listener for when it becomes ready
    const originalFbAsyncInit = window.fbAsyncInit;
    window.fbAsyncInit = function() {
      if (originalFbAsyncInit) originalFbAsyncInit();
      window.FB?.init({
        appId: import.meta.env.VITE_FACEBOOK_APP_ID,
        cookie: true,
        xfbml: true,
        version: 'v18.0'
      });
      setFbSDKReady(true);
    };

    // Also check periodically in case fbAsyncInit was already called
    const intervalId = setInterval(() => {
      if (checkFBSDK()) {
        clearInterval(intervalId);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  // Google OAuth handler - simplified and cleaned
  const handleGoogleLogin = async () => {
    if (!acceptedTerms) {
      setTermsError(true);
      return;
    }

    setTermsError(false);
    setIsGoogleLoading(true);

    try {
      const response = await authApi.getGoogleUrl();
      window.location.href = response.url;
    } catch (error) {
      console.error('Google login error:', error);
      toast({
        variant: "destructive",
        title: "Lỗi đăng nhập",
        description: "Không thể kết nối với Google. Vui lòng thử lại.",
      });
      setIsGoogleLoading(false);
    }
  };

  // Facebook login handler - completely rewritten
  const handleFacebookLogin = () => {
    if (!acceptedTerms) {
      setTermsError(true);
      return;
    }

    if (!fbSDKReady || !window.FB) {
      toast({
        variant: "destructive",
        title: "Facebook SDK chưa sẵn sàng",
        description: "Vui lòng đợi một chút và thử lại.",
      });
      return;
    }

    // Standard Facebook login flow
    window.FB.login(function(response) {
      if (response.status === 'connected' && response.authResponse) {
        // Get the access token
        const accessToken = response.authResponse.accessToken;

        // Call the backend API
        facebookLoginMutation.mutate(accessToken, {
          onSuccess: (result) => {
            setAuthState({
              user: result.user,
              isAuthenticated: true,
              isLoading: false,
              error: null
            });
            navigate('/location');
          },
          onError: (error) => {
            toast({
              variant: "destructive",
              title: "Đăng nhập thất bại",
              description: error instanceof Error ? error.message : "Có lỗi xảy ra khi đăng nhập với Facebook.",
            });
          }
        });
      } else {
        toast({
          variant: "destructive",
          title: "Đăng nhập thất bại",
          description: "Facebook login failed hoặc bị hủy",
        });
      }
    }, {scope: 'public_profile,email'});
  };

  // Handle login attempts
  const handleLogin = (provider: string) => {
    if (!acceptedTerms) {
      setTermsError(true);
      return;
    }

    setTermsError(false);

    // For Google login
    if (provider === "Google") {
      handleGoogleLogin();
      return;
    }

    // For Facebook login
    if (provider === "Facebook") {
      handleFacebookLogin();
      return;
    }

    // For other providers (to be implemented)
    toast({
      title: "Chưa hỗ trợ",
      description: `Đăng nhập bằng ${provider} sẽ được hỗ trợ trong thời gian tới.`,
    });
  };

  return (
    <div className="min-h-screen flex flex-col  text-white">
      {/* Header */}
      <header className="w-full  py-0 px-3">
        <div className="container mx-auto flex justify-end">
          {/* Language Selector */}
          <div className="relative inline-block">
            <button
              className="flex items-center space-x-1.5 text-[#FFFFFF] bg-transparent hover:bg-[#e8d9ca] rounded-lg px-2 py-1 transition-colors"
              onClick={() => setLanguage(language === "ENG" ? "VIE" : "ENG")}
            >
              <div className="w-4 h-4 rounded-full overflow-hidden flex-shrink-0">
                <img
                  src="/icons/uk-flag.svg"
                  alt="Language"
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="text-sm font-medium">{language}</span>
              <ChevronDown className="w-3 h-3" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex justify-center items-center py-6 pb-8">
        <div className="w-full max-w-[428px] px-4">
          <div className="space-y-8">
            {/* Camera Icon */}
            <div className="flex justify-center">
              <img
                src="/icons/camera-illustration.svg"
                alt="Camera"
                className="w-24 h-24 object-contain"
              />
            </div>

            {/* Heading */}
            <div className="text-center">
              <h1 className="text-2xl font-bold tracking-tight leading-tight font-manrope">
                Khám phá khu vực của bạn
              </h1>
              {invitationCode && (
                <p className="mt-2 text-sm text-[#FF8E73] font-manrope">
                  Đăng ký với mã mời: {invitationCode}
                </p>
              )}
            </div>

            {/* Login Options */}
            <div className="space-y-3">
              <Button
                onClick={() => handleLogin("Google")}
                disabled={isGoogleLoading}
                className="w-full h-[44px] bg-[#F2DDCC] text-black hover:bg-[#eacfb7] flex items-center justify-center space-x-2 rounded-full transition-colors text-sm disabled:opacity-50"
              >
                <img src="/icons/google.svg" alt="Google" className="w-4 h-4 font-sora" />
                <span>{isGoogleLoading ? "Đang chuyển hướng..." : "Tiếp tục với Google"}</span>
              </Button>

              <Button
                onClick={() => handleLogin("Facebook")}
                className="w-full h-[44px] bg-[#304A49] hover:bg-[#3a5857] text-white flex items-center justify-center space-x-2 rounded-full border border-[#446361] transition-colors text-sm"
                disabled={!fbSDKReady}
              >
                <img src="/icons/facebook.svg" alt="Facebook" className="w-4 h-4 font-sora" />
                <span>Tiếp tục với Facebook</span>
              </Button>


            </div>

            {/* Error display from auth store */}
            {authError && (
              <div className="flex items-center space-x-1 text-red-500 text-xs mt-1 bg-red-500/10 p-2 rounded">
                <AlertCircle size={14} />
                <span>{authError}</span>
              </div>
            )}

            {/* Terms and Conditions */}
            <div className="flex items-start space-x-2">
              <Checkbox
                id="terms"
                checked={acceptedTerms}
                onCheckedChange={(checked: boolean) => {
                  setAcceptedTerms(checked);
                  if (checked) setTermsError(false);
                }}
                className={`mt-0.5 ${termsError ? 'border-red-500' : ''}`}
              />
              <div>
                <label htmlFor="terms" className="text-xs font-sora">
                  Để tiếp tục đăng ký, bạn hãy đồng ý với{" "}
                  <Link to="/terms" className="text-[#FF8E73] hover:underline font-sora">Chính sách Riêng tư</Link>,{" "}
                  <Link to="/cookies" className="text-[#FF8E73] hover:underline font-sora">Chính sách Cookie</Link> và{" "}
                  <Link to="/agreement" className="text-[#FF8E73] hover:underline font-sora">Thỏa thuận Thành viên</Link> của chúng tôi
                </label>
                {termsError && (
                  <div className="flex items-center space-x-1 text-red-500 text-xs mt-1 font-sora">
                    <AlertCircle size={12} />
                    <span>Vui lòng đồng ý với điều khoản để tiếp tục</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}